import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import {
  ArrowRight,
  BadgeCheck,
  Building2,
  CheckCircle2,
  KeyRound,
  LockKeyhole,
  MailCheck,
  UserRound,
  Eye,
  EyeOff,
  Hammer,
  ShieldCheck,
} from "lucide-react";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { toast } from "sonner";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authCopy } from "@/lib/auth.copy";
import {
  completeAuthCallback,
  registerProviderAccount,
  resendAccountConfirmation,
  signInProvider,
  type AuthFailureReason,
} from "@/lib/auth.functions";
import { isSafeInternalPath } from "@/lib/auth.types";
import type { RegistrationAccountRole } from "@/lib/auth.types";
import { useLanguage } from "@/lib/i18n";

type LoginMode = "anmelden" | "registrieren" | "bestaetigt";
type LoginSearch = {
  mode?: LoginMode;
  code?: string;
  sb_flow_id?: string;
  next?: string;
  typ?: RegistrationAccountRole;
  accountType?: RegistrationAccountRole;
  role?: RegistrationAccountRole;
};

function registrationRole(value: unknown): RegistrationAccountRole | undefined {
  return value === "kunde" || value === "auftragnehmer" || value === "immobilienmakler"
    ? value
    : undefined;
}

export const Route = createFileRoute("/login")({
  staticData: { sitemap: false, toasts: true },
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    mode:
      search.mode === "registrieren" || search.mode === "bestaetigt"
        ? (search.mode as LoginMode)
        : ("anmelden" as LoginMode),
    code: typeof search.code === "string" ? search.code.slice(0, 2048) : undefined,
    sb_flow_id:
      typeof search.sb_flow_id === "string" ? search.sb_flow_id.slice(0, 2048) : undefined,
    next:
      typeof search.next === "string" && isSafeInternalPath(search.next) ? search.next : undefined,
    typ:
      registrationRole(search.typ) ??
      registrationRole(search.accountType) ??
      registrationRole(search.role),
    accountType: registrationRole(search.accountType),
    role: registrationRole(search.role),
  }),
  head: () => ({
    meta: [
      { title: "Login und Registrierung. Begner" },
      {
        name: "description",
        content: "Sicher bei Begner anmelden oder ein persönliches Konto erstellen.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: LoginPage,
});

function LoginPage() {
  const { language } = useLanguage();
  const copy = authCopy[language].login;
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const callback = useServerFn(completeAuthCallback);
  const callbackStarted = useRef(false);

  const [mode, setMode] = useState<Exclude<LoginMode, "bestaetigt">>(
    search.mode === "registrieren" ? "registrieren" : "anmelden",
  );
  const [callbackError, setCallbackError] = useState<AuthFailureReason | null>(null);

  useEffect(() => {
    if (!search.code || callbackStarted.current) return;
    callbackStarted.current = true;

    callback({ data: { code: search.code, flowId: search.sb_flow_id } })
      .then((result) => {
        if (!result.ok) {
          setCallbackError(result.reason);
          return;
        }
        if (search.next) {
          window.location.replace(search.next);
        } else {
          void navigate({ to: "/konto", replace: true });
        }
      })
      .catch(() => setCallbackError("unknown"));
  }, [callback, navigate, search.code, search.next, search.sb_flow_id]);

  if (search.code && !callbackError) {
    return (
      <AuthPageShell>
        <Card className="mx-auto w-full max-w-xl overflow-hidden border-border/70 shadow-card">
          <CardContent className="flex flex-col items-center px-6 py-14 text-center sm:px-12">
            <div className="relative mb-6 grid size-16 place-items-center rounded-2xl bg-brand-soft text-primary">
              <MailCheck className="size-7" />
              <span className="absolute -right-1 -top-1 size-3 animate-pulse rounded-full bg-primary ring-4 ring-background" />
            </div>
            <h1 className="font-display text-3xl font-bold tracking-tight">{copy.callbackTitle}</h1>
            <p className="mt-3 max-w-md text-muted-foreground">{copy.callbackText}</p>
          </CardContent>
        </Card>
      </AuthPageShell>
    );
  }

  return (
    <AuthPageShell>
      <div className="grid items-start gap-10 lg:grid-cols-[minmax(0,0.9fr)_minmax(460px,0.75fr)] lg:gap-16">
        <section className="pt-4 lg:sticky lg:top-28 lg:pt-14">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/15 bg-brand-soft px-3 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-brand-deep">
            <BadgeCheck className="size-4" />
            {copy.eyebrow}
          </div>
          <h1 className="mt-6 max-w-2xl font-display text-3xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-6xl">
            {copy.title}
          </h1>
          <p className="mt-5 max-w-xl text-lg leading-8 text-muted-foreground">{copy.intro}</p>

          <div className="mt-9 grid gap-3 sm:grid-cols-3 lg:max-w-2xl">
            <TrustPoint icon={<MailCheck />} label={copy.confirmationTitle} />
            <TrustPoint icon={<ShieldCheck />} label={copy.secure} />
            <TrustPoint icon={<BadgeCheck />} label={authCopy[language].account.status.approved} />
          </div>
        </section>

        <Card className="overflow-hidden border-border/70 bg-card/95 shadow-[0_24px_70px_-30px_hsl(var(--primary)/0.38)] backdrop-blur">
          <div className="grid grid-cols-2 border-b border-border/70 bg-muted/30 p-1.5">
            <button
              type="button"
              onClick={() => {
                setMode("anmelden");
                setCallbackError(null);
              }}
              className={`rounded-xl px-4 py-3 text-sm font-bold transition ${
                mode === "anmelden"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {copy.loginTab}
            </button>
            <button
              type="button"
              onClick={() => {
                setMode("registrieren");
                setCallbackError(null);
              }}
              className={`rounded-xl px-4 py-3 text-sm font-bold transition ${
                mode === "registrieren"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {copy.registerTab}
            </button>
          </div>

          <CardContent className="p-6 sm:p-8">
            {callbackError && (
              <Alert variant="destructive" className="mb-6">
                <AlertDescription>{copy.errors[callbackError]}</AlertDescription>
              </Alert>
            )}
            {mode === "anmelden" ? (
              <LoginForm next={search.next} onRegister={() => setMode("registrieren")} />
            ) : (
              <RegistrationForm
                next={search.next}
                initialAccountType={search.typ}
                onLogin={() => setMode("anmelden")}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </AuthPageShell>
  );
}

function AuthPageShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative isolate overflow-hidden bg-[linear-gradient(145deg,hsl(var(--background)),hsl(var(--brand-soft))_62%,hsl(var(--background)))] py-12 sm:py-20">
      <div className="pointer-events-none absolute -left-32 top-20 -z-10 size-96 rounded-full bg-primary/10 blur-3xl" />
      <div className="pointer-events-none absolute -right-32 bottom-10 -z-10 size-96 rounded-full bg-brand/15 blur-3xl" />
      <div className="container-page">{children}</div>
    </div>
  );
}

function TrustPoint({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div className="flex min-h-24 flex-col gap-3 rounded-2xl border border-border/70 bg-background/70 p-4 text-sm font-semibold leading-5 shadow-sm backdrop-blur">
      <span className="text-primary [&_svg]:size-5">{icon}</span>
      <span>{label}</span>
    </div>
  );
}

function LoginForm({ next, onRegister }: { next?: string; onRegister: () => void }) {
  const { language } = useLanguage();
  const copy = authCopy[language].login;
  const signIn = useServerFn(signInProvider);
  const navigate = Route.useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<AuthFailureReason | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      const result = await signIn({ data: { email, password } });
      if (!result.ok) {
        setError(result.reason);
        return;
      }
      if (next) {
        window.location.assign(next);
      } else {
        await navigate({ to: "/konto" });
      }
    } catch {
      setError("unknown");
    } finally {
      setPending(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-5">
      <div>
        <h2 className="font-display text-2xl font-bold">{copy.loginTab}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{copy.secure}</p>
      </div>
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{copy.errors[error]}</AlertDescription>
        </Alert>
      )}
      <div className="space-y-2">
        <Label htmlFor="login-email">
          {copy.email} <RequiredMark />
        </Label>
        <Input
          id="login-email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
          className="h-12"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="login-password">
          {copy.password} <RequiredMark />
        </Label>
        <div className="relative">
          <Input
            id="login-password"
            type={passwordVisible ? "text" : "password"}
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
            className="h-12 pr-11"
          />
          <PasswordVisibilityButton
            visible={passwordVisible}
            onClick={() => setPasswordVisible((visible) => !visible)}
          />
        </div>
      </div>
      <Button type="submit" size="lg" className="h-12 w-full shadow-brand" disabled={pending}>
        {pending ? copy.pending : copy.submitLogin}
        {!pending && <ArrowRight className="ml-1 size-4" />}
      </Button>
      <p className="text-center text-sm text-muted-foreground">
        {copy.newHere}{" "}
        <button
          type="button"
          onClick={onRegister}
          className="font-bold text-primary hover:underline"
        >
          {copy.registerTab}
        </button>
      </p>
    </form>
  );
}

function RegistrationForm({
  next,
  initialAccountType,
  onLogin,
}: {
  next?: string;
  initialAccountType?: RegistrationAccountRole;
  onLogin: () => void;
}) {
  const { language } = useLanguage();
  const copy = authCopy[language].login;
  const register = useServerFn(registerProviderAccount);
  const resend = useServerFn(resendAccountConfirmation);
  const [form, setForm] = useState({
    accountType: initialAccountType ?? ("kunde" as RegistrationAccountRole),
    firstName: "",
    lastName: "",
    companyName: "",
    phone: "",
    email: "",
    password: "",
  });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [pending, setPending] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState<AuthFailureReason | null>(null);
  const [confirmationEmail, setConfirmationEmail] = useState<string | null>(null);
  const [passwordVisible, setPasswordVisible] = useState(false);

  function update(field: keyof typeof form, value: string) {
    setForm((current) => ({ ...current, [field]: value }));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!termsAccepted) return;
    setPending(true);
    setError(null);
    try {
      const result = await register({
        data: {
          ...form,
          phone: form.phone || undefined,
          termsAccepted: true,
          next,
        },
      });
      if (!result.ok) {
        setError(result.reason);
        return;
      }
      if (!result.confirmationRequired) {
        window.location.assign(next ?? "/konto");
        return;
      }
      setConfirmationEmail(form.email);
    } catch {
      setError("unknown");
    } finally {
      setPending(false);
    }
  }

  async function resendConfirmation() {
    if (!confirmationEmail) return;
    setResending(true);
    try {
      const result = await resend({ data: { email: confirmationEmail, next } });
      if (result.ok) toast.success(copy.resent);
      else setError(result.reason);
    } catch {
      setError("unknown");
    } finally {
      setResending(false);
    }
  }

  if (confirmationEmail) {
    return (
      <div className="py-3 text-center">
        <div className="mx-auto grid size-14 place-items-center rounded-2xl bg-emerald-500/10 text-emerald-700">
          <MailCheck className="size-6" />
        </div>
        <p className="mt-5 text-xs font-bold uppercase tracking-[0.16em] text-primary">
          {copy.confirmationEyebrow}
        </p>
        <h2 className="mt-2 font-display text-2xl font-bold">{copy.confirmationTitle}</h2>
        <p className="mt-3 text-sm leading-6 text-muted-foreground">{copy.confirmationText}</p>
        <div className="mt-5 rounded-xl border border-border bg-muted/35 px-4 py-3 text-sm font-semibold">
          {confirmationEmail}
        </div>
        <p className="mt-4 text-xs text-muted-foreground">{copy.confirmationHint}</p>
        <Button
          variant="outline"
          className="mt-6 w-full"
          onClick={resendConfirmation}
          disabled={resending}
        >
          {resending ? copy.pending : copy.resend}
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-5">
      <div>
        <h2 className="font-display text-2xl font-bold">{copy.registerTab}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{copy.passwordHint}</p>
      </div>
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{copy.errors[error]}</AlertDescription>
        </Alert>
      )}
      <fieldset className="space-y-2">
        <legend className="text-sm font-medium">
          {copy.accountType} <RequiredMark />
        </legend>
        <div className="grid gap-3 sm:grid-cols-3">
          <AccountTypeButton
            active={form.accountType === "kunde"}
            icon={<UserRound />}
            label={copy.customer}
            onClick={() => update("accountType", "kunde")}
          />
          <AccountTypeButton
            active={form.accountType === "auftragnehmer"}
            icon={<Hammer />}
            label={copy.contractor}
            onClick={() => update("accountType", "auftragnehmer")}
          />
          <AccountTypeButton
            active={form.accountType === "immobilienmakler"}
            icon={<Building2 />}
            label={copy.realEstateAgent}
            onClick={() => update("accountType", "immobilienmakler")}
          />
        </div>
      </fieldset>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field id="register-first-name" label={copy.firstName} required>
          <Input
            id="register-first-name"
            autoComplete="given-name"
            value={form.firstName}
            onChange={(event) => update("firstName", event.target.value)}
            required
            className="h-11"
          />
        </Field>
        <Field id="register-last-name" label={copy.lastName} required>
          <Input
            id="register-last-name"
            autoComplete="family-name"
            value={form.lastName}
            onChange={(event) => update("lastName", event.target.value)}
            required
            className="h-11"
          />
        </Field>
      </div>
      {form.accountType !== "kunde" ? (
        <Field id="register-company" label={copy.company} required>
          <Input
            id="register-company"
            autoComplete="organization"
            value={form.companyName}
            onChange={(event) => update("companyName", event.target.value)}
            required
            className="h-11"
          />
        </Field>
      ) : null}
      <Field id="register-phone" label={`${copy.phone} (${copy.optional})`}>
        <Input
          id="register-phone"
          type="tel"
          autoComplete="tel"
          value={form.phone}
          onChange={(event) => update("phone", event.target.value)}
          className="h-11"
        />
      </Field>
      <Field id="register-email" label={copy.email} required>
        <Input
          id="register-email"
          type="email"
          autoComplete="email"
          value={form.email}
          onChange={(event) => update("email", event.target.value)}
          required
          className="h-11"
        />
      </Field>
      <Field id="register-password" label={copy.password} hint={copy.passwordHint} required>
        <div className="relative">
          <Input
            id="register-password"
            type={passwordVisible ? "text" : "password"}
            autoComplete="new-password"
            minLength={8}
            maxLength={128}
            value={form.password}
            onChange={(event) => update("password", event.target.value)}
            required
            className="h-11 pr-11"
          />
          <PasswordVisibilityButton
            visible={passwordVisible}
            onClick={() => setPasswordVisible((visible) => !visible)}
          />
        </div>
      </Field>
      <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-border bg-muted/25 p-4 text-sm leading-5">
        <Checkbox
          checked={termsAccepted}
          onCheckedChange={(checked) => setTermsAccepted(checked === true)}
          className="mt-0.5"
        />
        <span>
          <RequiredMark /> {copy.terms}
        </span>
      </label>
      <Button
        type="submit"
        size="lg"
        className="h-12 w-full shadow-brand"
        disabled={pending || !termsAccepted}
      >
        {pending ? copy.pending : copy.submitRegister}
        {!pending && <ArrowRight className="ml-1 size-4" />}
      </Button>
      <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
        <LockKeyhole className="size-3.5" />
        {copy.secure}
      </div>
      <p className="text-center text-sm text-muted-foreground">
        {copy.alreadyRegistered}{" "}
        <button type="button" onClick={onLogin} className="font-bold text-primary hover:underline">
          {copy.loginTab}
        </button>
      </p>
    </form>
  );
}

function AccountTypeButton({
  active,
  icon,
  label,
  onClick,
}: {
  active: boolean;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`flex items-center gap-3 rounded-xl border p-3 text-left text-sm font-semibold transition-colors ${
        active
          ? "border-primary bg-brand-soft text-brand-deep"
          : "border-border bg-background hover:border-primary/40"
      }`}
    >
      <span className="shrink-0 text-primary [&_svg]:size-4">{icon}</span>
      <span className="min-w-0 break-words">{label}</span>
    </button>
  );
}

function PasswordVisibilityButton({ visible, onClick }: { visible: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="absolute inset-y-0 right-0 grid w-11 place-items-center rounded-r-md text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      aria-label={visible ? "Passwort verbergen" : "Passwort anzeigen"}
      aria-pressed={visible}
    >
      {visible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
    </button>
  );
}

function Field({
  id,
  label,
  hint,
  required = false,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-end justify-between gap-3">
        <Label htmlFor={id}>
          {label} {required && <RequiredMark />}
        </Label>
        {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

function RequiredMark() {
  return (
    <span className="text-destructive" aria-hidden="true">
      *
    </span>
  );
}
